import {
  CompanyUsersOrderField,
  SortOption,
  useCompanyUsersFindManyPaginatedQuery,
} from 'generated/graphql';
import { debounce } from 'lodash';
import { useMemo, useState } from 'react';
import { SelectFormField, TSelectOption } from '@spotted-zebra-uk/ui-components';

// The Spotted Zebra internal company — its members are the SZ team members
// eligible to be assigned as a Customer Success Manager.
const SPOTTED_ZEBRA_COMPANY_ID = 1;

interface ICsmUserSelect {
  value?: TSelectOption<number>;
  onChange: (option?: TSelectOption<number>) => void;
}

const CsmUserSelect = ({ value, onChange }: ICsmUserSelect) => {
  const [search, setSearch] = useState('');
  const debouncedSetSearch = useMemo(() => debounce(setSearch, 500), []);

  const { data, loading } = useCompanyUsersFindManyPaginatedQuery({
    fetchPolicy: 'network-only',
    variables: {
      args: { companyId: SPOTTED_ZEBRA_COMPANY_ID, search },
      order: { field: CompanyUsersOrderField.Name, direction: SortOption.Asc },
      paginate: { page: 1, size: 100 },
    },
  });

  const options: TSelectOption<number>[] = useMemo(
    () =>
      (data?.companyUsers.data ?? []).flatMap(({ user }) =>
        user
          ? [
              {
                label: `${user.firstName} ${user.lastName}`,
                value: user.id,
                labelNote: user.email,
              },
            ]
          : []
      ),
    [data]
  );

  return (
    <SelectFormField<number>
      id="csm-user"
      name="csmUser"
      label="Customer Success Manager"
      placeholder="Search Spotted Zebra team members"
      value={value}
      options={options}
      onChange={(option: TSelectOption<number>) =>
        onChange(option?.value ? option : undefined)
      }
      onInputChange={debouncedSetSearch}
      onClearBtnClick={() => onChange(undefined)}
      isLoading={loading}
      isSearchable
      disableFilter
      hasClearIndicator
    />
  );
};

export default CsmUserSelect;
